/**
 * useDemoTracking — anonymous viewer-token tracking for the landing demo.
 * ========================================================================
 * Generates (and persists) a stable `cartie_viewer_token` in localStorage
 * and exposes a `track(eventName, metadata)` helper that batches/debounces
 * POST /api/landing-demo/track calls.
 *
 * Design:
 *   - Pure client. No PII. Token is a UUIDv4.
 *   - Batches up to 25 events per HTTP call with a 1500ms debounce so a
 *     burst (act 1 → act 2 → act 3) becomes a single network call.
 *   - Best-effort: tracking failures NEVER break the UI. Errors logged
 *     to console.debug only.
 *   - Returns `attributeSignup(userEmail)` for the signup flow to call
 *     when a fresh account is created.
 *
 * Usage:
 *   const { track, viewerToken, attributeSignup } = useDemoTracking();
 *   track('walkthrough_viewed');
 *   track('walkthrough_act_3_reached', { actIndex: 2 });
 *   await attributeSignup('user@example.com');
 */
import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'cartie_viewer_token';
const BATCH_DEBOUNCE_MS = 1500;
const MAX_BATCH = 25;

const getOrCreateViewerToken = () => {
  try {
    let t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      t = (crypto?.randomUUID?.() || (Math.random().toString(36).slice(2) + Date.now().toString(36)));
      localStorage.setItem(TOKEN_KEY, t);
    }
    return t;
  } catch {
    // fallback for hostile browsers / private mode
    return `anon-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
};

export const useDemoTracking = () => {
  const viewerToken = useRef(null);
  const queue = useRef([]);
  const timer = useRef(null);
  const flushing = useRef(false);

  if (!viewerToken.current) {
    viewerToken.current = getOrCreateViewerToken();
  }

  const flush = useCallback(async () => {
    if (flushing.current || queue.current.length === 0) return;
    flushing.current = true;
    const batch = queue.current.splice(0, MAX_BATCH);
    try {
      await axios.post(
        `${API}/landing-demo/track`,
        {
          viewer_token: viewerToken.current,
          events: batch,
          referrer: document.referrer?.slice(0, 400) || null,
          landing_path: window.location.pathname?.slice(0, 200) || null,
        },
        { timeout: 4000 }
      );
    } catch (e) {
      // best-effort — never disrupt UX
      console.debug('[demo-tracking] flush failed', e?.message);
    } finally {
      flushing.current = false;
      if (queue.current.length > 0) {
        // keep draining if more events arrived during flight
        scheduleFlush();
      }
    }
  }, []);

  const scheduleFlush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, BATCH_DEBOUNCE_MS);
  }, [flush]);

  const track = useCallback((eventName, metadata) => {
    if (!eventName) return;
    queue.current.push({ event_name: eventName, metadata: metadata || null });
    // Immediate flush for terminal/completion events so we never lose them
    if (
      eventName === 'walkthrough_completed' ||
      eventName === 'walkthrough_cta_post_demo_clicked'
    ) {
      if (timer.current) clearTimeout(timer.current);
      flush();
    } else {
      scheduleFlush();
    }
  }, [flush, scheduleFlush]);

  // Flush remaining events on unload — best-effort via sendBeacon
  useEffect(() => {
    const handler = () => {
      if (queue.current.length === 0) return;
      try {
        const body = JSON.stringify({
          viewer_token: viewerToken.current,
          events: queue.current.splice(0, MAX_BATCH),
          referrer: document.referrer?.slice(0, 400) || null,
          landing_path: window.location.pathname?.slice(0, 200) || null,
        });
        navigator.sendBeacon?.(
          `${API}/landing-demo/track`,
          new Blob([body], { type: 'application/json' })
        );
      } catch { /* swallow */ }
    };
    window.addEventListener('pagehide', handler);
    return () => window.removeEventListener('pagehide', handler);
  }, []);

  const attributeSignup = useCallback(async (userEmail) => {
    if (!userEmail) return;
    try {
      await axios.post(
        `${API}/landing-demo/attribute-signup`,
        { viewer_token: viewerToken.current, user_email: userEmail },
        { timeout: 4000 }
      );
    } catch (e) {
      console.debug('[demo-tracking] attribute failed', e?.message);
    }
  }, []);

  return { track, viewerToken: viewerToken.current, attributeSignup };
};

export default useDemoTracking;
